import { Calendar, FilePenLine, Play } from "lucide-react";
import moment from "moment";
import { FC, useCallback, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

import { TransactionsTypes } from "@/app/dashboard/transactions/types";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import * as m from "@/paraglide/messages";
import { getDashboardHoldings } from "@/services/controllers/dashboard";
import {
  updatePaperTransaction,
  getPaperDatePrice,
  deletePaperTransaction,
} from "@/services/controllers/trade";
import { useAppSelector } from "@/services/hooks/redux.hooks";

import SearchCoin from "./coinInput";
import PairInput from "./pairInput";
import LoadingSpinner from "../loading-spinner/loading-spinner";
import { Calendar as CalendarView } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { useToast } from "../ui/use-toast";

const EditPaperTransaction: FC<{ selectedItem: TransactionsTypes }> = ({
  selectedItem,
}) => {
  const { selectedExchange } = useAppSelector(
    (state) => state.exchangeSwitcher,
  );
  const { toast } = useToast();

  const [selectedType, setSelectedType] = useState<"buy" | "sell" | undefined>(
    "buy",
  );
  const [selectedCoin, setSelectedCoin] = useState<TransactionsTypes>();
  const [selectedPair, setSelectedPair] = useState<any>({});
  const [time, setTime] = useState<string>("");
  const [price, setPrice] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [fees, setFees] = useState<string>("0");
  const [affectUSDT, setAffectUSDT] = useState<boolean>(false);
  const [dateTemp, setDateTemp] = useState<string>("");
  const [showCalendar, setShowCalendar] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  useEffect(() => {
    if (selectedItem?.id != null && showDialog) {
      setSelectedType(
        selectedItem?.order_data.side?.toLowerCase().toString() as any,
      );
      setSelectedCoin(selectedItem);
      setSelectedPair({
        name: selectedItem?.order_symbol,
        quote_asset_id: selectedItem?.asset_name,
        base_asset: selectedItem?.base_name.toUpperCase(),
      });
      setDateTemp(
        new Date(selectedItem?.settled_at ?? "").toISOString().split("T")[0],
      );
      setTime(
        new Date(selectedItem?.settled_at ?? "")
          .toTimeString()
          .split(" ")[0]
          .slice(0, -3),
      );
      setFees(selectedItem.total_fees_applied.cost.toString() ?? "");
      setDateTemp(
        new Date(selectedItem?.settled_at ?? "").toISOString().split("T")[0],
      );
      setAmount(selectedItem?.executed_amount.toString() ?? "");
      setPrice(selectedItem?.current_price.toString());

      if (typeof window !== "undefined") {
        const dateInput: any = document.getElementById("date-edit-input");

        if (dateInput)
          dateInput.value = new Date(selectedItem?.settled_at ?? "")
            .toISOString()
            .split("T")[0];
      }
    }
  }, [selectedItem, showDialog]);

  useEffect(() => {
    const validYear = parseInt(dateTemp.split("-")?.[0]).toString().length > 3;
    if (selectedCoin?.asset_name && validYear) {
      getPaperDatePrice(
        selectedCoin?.asset_name.toUpperCase(),
        dateTemp,
        selectedPair?.base_asset ?? "USDT",
      ).then((response: any) => {
        if (response.data.data.price) {
          setPrice(parseFloat(response.data.data.price).toString());
        } else {
          setPrice(selectedItem.current_price.toString());
        }
      });
    }
  }, [selectedCoin, selectedPair, dateTemp, selectedItem.current_price]);

  const reset = useCallback(() => {
    setSelectedType("buy");
    setSelectedCoin(undefined);
    setSelectedPair(undefined);
    setDateTemp(new Date().toString());
    setTime(new Date().toString());
    setFees("");
    setAmount("");
    setPrice("");
  }, []);

  const onSave = useCallback(() => {
    setIsSaving(true);
    const dateInput: any = document.getElementById("date-edit-input");
    const date = dateInput.value;

    if (
      selectedExchange?.exchange_key_id &&
      selectedItem.id &&
      date !== "" &&
      date != null
    )
      updatePaperTransaction(selectedItem.id, {
        amount,
        price: price as any,
        side: selectedType?.toUpperCase() as any,
        settled_at: new Date(`${date}T${time}:00.000Z`) as any,
        asset_name: selectedCoin?.asset_name.toUpperCase() as string,
        base_name: selectedPair.base_asset,
        symbol_name: selectedPair.name,
        affect: affectUSDT,
        fees,
      })
        .then(() => {
          toast({
            description: m.watery_even_lizard_express(),
            variant: "success",
            duration: 2000,
          });
          window.dispatchEvent(
            new CustomEvent("refetch-orders", {
              detail: { key: selectedExchange.exchange_key_id },
            }),
          );
          setShowDialog(false);
          getDashboardHoldings({
            params: {
              exchange_key_id: selectedExchange?.exchange_key_id,
            },
          });
        })
        .finally(() => {
          setIsSaving(false);
        });
  }, [
    affectUSDT,
    amount,
    fees,
    price,
    selectedCoin?.asset_name,
    selectedExchange?.exchange_key_id,
    selectedItem?.id,
    selectedPair?.base_asset,
    selectedPair?.name,
    selectedType,
    time,
    toast,
  ]);

  const TypeCTA = useCallback(
    (type: "buy" | "sell") => {
      const isActive = selectedType === type;

      const onClick = () => {
        setSelectedType(type);
      };

      return (
        <button
          className={twMerge(
            isActive
              ? type === "buy"
                ? "opacity-100 bg-success bg-opacity-20 border-success-foreground"
                : "opacity-100 bg-destructive bg-opacity-20 border-destructive-foreground"
              : "opacity-40 bg-gray-100 border-gray-200",
            " h-[34px] w-[80px] border-solid border-[1px] rounded-sm",
          )}
          onClick={onClick}
        >
          <div
            className={twMerge(
              isActive
                ? type === "buy"
                  ? "text-success-foreground"
                  : "text-destructive-foreground"
                : "",
              "gap-1 font-bold justify-center text-sm flex items-center",
            )}
          >
            <Play
              className={twMerge(
                type === "buy" ? "-rotate-90" : "rotate-90",
                isActive
                  ? type === "buy"
                    ? "fill-success-foreground"
                    : "fill-destructive-foreground"
                  : "fill-gray-600",
                `w-3 h-3 stroke-0`,
              )}
            />
            {type === "buy" ? m.buy() : m.sell()}
          </div>
        </button>
      );
    },
    [selectedType],
  );

  const onDelete = useCallback(() => {
    setIsDeleting(true);
    deletePaperTransaction(selectedItem?.id)
      .then(() => {
        toast({
          description: m.jumpy_sound_gibbon_play(),
          variant: "success",
          duration: 2000,
        });
        window.dispatchEvent(
          new CustomEvent("refetch-orders", {
            detail: { key: selectedExchange?.exchange_key_id },
          }),
        );
        getDashboardHoldings({
          params: {
            exchange_key_id: selectedExchange?.exchange_key_id,
          },
        });
        setShowDialog(false);
      })
      .finally(() => setIsDeleting(false));
  }, [selectedExchange?.exchange_key_id, selectedItem?.id, toast]);

  return (
    <Dialog
      open={showDialog}
      onOpenChange={(op) => {
        setShowDialog(op);
        if (!op) reset();
      }}
    >
      <DialogTrigger asChild>
        <FilePenLine className="w-4 h-auto fill-black-1 cursor-pointer" />
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Transaction</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="flex gap-4 items-center justify-center">
            {TypeCTA("buy")}
            {TypeCTA("sell")}
          </div>
          <div className="space-y-2">
            <Label htmlFor="coin">Coin</Label>
            <SearchCoin
              onSelectCoin={setSelectedCoin}
              defaultItem={{
                id: selectedItem.id,
                asset_data: {
                  image: `https://logo.twelvedata.com/crypto/${selectedItem?.order_symbol
                    .toLowerCase()
                    .replace(selectedItem.base_name.toLowerCase(), "")
                    .replace(/[^a-zA-Z0-9 ]/g, "")}.png`,
                  name: selectedItem.asset_name,
                  current_price: selectedItem.current_price,
                },
                asset_name: selectedItem.asset_name,
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="exchange-pair">{m.ExchangePair()}</Label>
            <PairInput
              coin={selectedCoin?.asset_name ?? ""}
              onClick={setSelectedPair}
              disabled={!selectedCoin?.asset_name}
              defaultItem={selectedPair}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date">{m.date()}</Label>
              <div className="relative" id="date">
                <Input
                  type="date"
                  id="date-edit-input"
                  className="pr-10"
                  value={dateTemp}
                  disabled={selectedCoin?.asset_name == null}
                  readOnly
                />
                <Popover open={showCalendar} onOpenChange={setShowCalendar}>
                  <PopoverTrigger
                    asChild
                    disabled={selectedCoin?.asset_name == null}
                  >
                    <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  </PopoverTrigger>
                  <PopoverContent>
                    <CalendarView
                      mode="single"
                      selected={new Date(dateTemp)}
                      disabled={selectedCoin?.asset_name == null}
                      toDate={new Date()}
                      onSelect={(date) => {
                        setDateTemp(moment(date).format("YYYY-MM-DD"));
                        setShowCalendar(false);
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="time">{m.time()}</Label>
              <Input
                id="time"
                className="w-full"
                type="time"
                value={time}
                max={new Date().toString()}
                onChange={(e) => {
                  setTime(e.target.value);
                }}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="amount">{m.amount()}</Label>
              <Input
                id="amount"
                placeholder="Enter Amount"
                value={amount}
                type="number"
                onChange={(e) => {
                  setAmount(e.target.value);
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="price">{m.price()}</Label>
              <div className="flex">
                <Input
                  id="price"
                  placeholder="Enter Price"
                  className="flex-1 border-r-0 rounded-r-none"
                  value={price}
                  type="number"
                  onChange={(e) => {
                    setPrice(e.target.value);
                  }}
                />
                <span className="inline-flex items-center px-3 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-500 sm:text-sm">
                  {selectedPair?.base_asset ?? "USD"}
                </span>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="fees">{`Fees ${
              selectedCoin?.asset_name && selectedPair?.base_asset
                ? `in ${
                    selectedType === "buy"
                      ? selectedCoin.asset_name.toUpperCase()
                      : selectedPair.base_asset
                  }`
                : ""
            }`}</Label>
            <Input
              id="fees"
              placeholder="Enter Amount"
              value={fees}
              type="number"
              onChange={(e) => {
                setFees(e.target.value);
              }}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="deduct"
              checked={affectUSDT}
              onCheckedChange={setAffectUSDT as any}
            />
            <Label htmlFor="deduct">
              {selectedType === "buy"
                ? m.deduct_from({
                    base_asset: selectedPair?.base_asset ?? "USDT",
                  })
                : m.add_to({ base_asset: selectedPair?.base_asset ?? "USDT" })}
            </Label>
          </div>
        </div>
        <DialogFooter className="flex justify-between">
          <Button
            variant="destructive"
            onClick={onDelete}
            disabled={isSaving || isDeleting}
          >
            {isDeleting ? <LoadingSpinner /> : m.watery_actual_jurgen_push()}
          </Button>
          <Button
            variant="outline"
            disabled={isSaving}
            onClick={() => setShowDialog(false)}
          >
            {m.cancel()}
          </Button>
          <Button
            disabled={
              isSaving ||
              selectedCoin?.asset_name == null ||
              selectedPair?.base_asset == null ||
              price?.trim() === "" ||
              time?.trim() === "" ||
              dateTemp?.trim() === "" ||
              amount?.trim() === "" ||
              selectedType == null ||
              fees.trim() === ""
            }
            onClick={onSave}
          >
            {isSaving ? <LoadingSpinner /> : m.update()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditPaperTransaction;
